import type { GridCellColor } from './model'
import type { GridRendererConfig, GridRgb } from './config'

const BASE_16: readonly GridRgb[] = [
  [0, 0, 0], [205, 0, 0], [0, 205, 0], [205, 205, 0],
  [0, 0, 238], [205, 0, 205], [0, 205, 205], [229, 229, 229],
  [127, 127, 127], [255, 0, 0], [0, 255, 0], [255, 255, 0],
  [92, 92, 255], [255, 0, 255], [0, 255, 255], [255, 255, 255],
]
const CUBE = [0, 95, 135, 175, 215, 255] as const

export function defaultPalette(): readonly GridRgb[] {
  const palette: GridRgb[] = BASE_16.map((color) => Object.freeze([...color]) as GridRgb)
  for (let index = 16; index < 232; index++) {
    const value = index - 16
    palette.push(Object.freeze([
      CUBE[Math.floor(value / 36) % 6],
      CUBE[Math.floor(value / 6) % 6],
      CUBE[value % 6],
    ]) as GridRgb)
  }
  for (let index = 232; index < 256; index++) {
    const value = 8 + 10 * (index - 232)
    palette.push(Object.freeze([value, value, value]) as GridRgb)
  }
  return Object.freeze(palette)
}

export function resolveColor(config: GridRendererConfig, color: GridCellColor,
                             foregroundRole: boolean): GridRgb {
  switch (color.type) {
    case 'default': return foregroundRole ? config.foreground : config.background
    case 'basic': return config.palette[color.value]
    case 'ansi256': return config.palette[color.value]
    case 'rgb': return color.value
  }
}

export function resolveCellColors(config: GridRendererConfig, foreground: GridCellColor,
                                  background: GridCellColor, reverse: boolean): {
  foreground: GridRgb
  background: GridRgb
} {
  // Defaults depend on the source role, so resolve both slots before swapping.
  const resolvedForeground = resolveColor(config, foreground, true)
  const resolvedBackground = resolveColor(config, background, false)
  return reverse
    ? { foreground: resolvedBackground, background: resolvedForeground }
    : { foreground: resolvedForeground, background: resolvedBackground }
}

export function colorsEqual(left: GridRgb, right: GridRgb): boolean {
  return left[0] === right[0] && left[1] === right[1] && left[2] === right[2]
}
